import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { getReceiptDetails } from '../../utilities/api';
import BillViewReceiptSidePanel from './BillViewReceiptSidePanel';
import useUser from '../../hooks/useUser';
import './billviewreceipt.css';

const BillViewReceipt = () => {
    const { id, branchId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const waiterIdFromNav = location.state?.userId ?? location.state?.user_id;
    const toast = useRef(null);
    const storedUserid = localStorage.getItem('userid');

    const [loading, setLoading] = useState(true);
    const [billData, setBillData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (id && branchId) {
            fetchBillData();
        } else if (id && !branchId) {
            // Handle missing branchId - show error instead of blank screen
            setLoading(false);
            setError('Branch information is missing. Please select a branch in the filter and try again.');
        }
    }, [id, branchId]);

    const fetchBillData = async () => {
        setLoading(true);
        setError(null);
        try {
            let data = await getReceiptDetails(id, branchId);

            if (typeof data === 'string') {
                data = JSON.parse(data);
            }
            if (data.final_output) {
                data = JSON.parse(data.final_output);
            }

            setBillData(data);
        } catch (err) {
            console.error('Error fetching bill data:', err);
            setError('Failed to load bill data');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load bill data',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    // Check if HSN should be displayed
    const shouldShowHSN = () => {
        if (!billData?.receipt_settings) return false;
        const hsnSetting = billData.receipt_settings.find((s) => s.setting_name === 'HSN_DISPLAY_OPTION');
        return hsnSetting?.setting_access === '1';
    };

    // Check invoice format (0 = default, 1 = alternate format)
    const getInvoiceFormat = () => {
        if (!billData?.receipt_settings) return 0;
        const formatSetting = billData.receipt_settings.find((s) => s.setting_name === 'COUNTER_INVOICE_FORMAT');
        return formatSetting?.setting_access === '1' ? 1 : 0;
    };

    // Check HSN or SAC display option (1 = HSN inline, 2 = SAC code at bottom)
    const getHsnSacDisplayOption = () => {
        if (!billData?.receipt_settings) return 1;
        const setting = billData.receipt_settings.find((s) => s.setting_name === 'HSN_OR_SAC_DISPLAY_OPTION');
        return setting?.setting_access === '2' ? 2 : 1;
    };

    // Get unique SAC/HSN code from items (for SAC display at bottom)
    const getUniqueSacCode = () => {
        const items = getOrderItems();
        const codes = new Set();
        items.forEach((item) => {
            if (item.hsn_code && item.hsn_code !== '0' && item.hsn_code !== 0) {
                codes.add(item.hsn_code);
            }
        });
        return Array.from(codes).join(', ');
    };

    // Check if item has valid HSN code
    const hasValidHSN = (hsnCode) => {
        return hsnCode && hsnCode !== '0' && hsnCode !== 0 && hsnCode !== '';
    };

    // Format currency
    const formatCurrency = (amount) => {
        const num = parseFloat(amount || 0);
        return num.toLocaleString('en-IN', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2
        });
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString || dateString === '0') return '';
        const parts = dateString.split('-');
        if (parts.length === 3) {
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        return dateString;
    };

    // Get order summary
    const getOrderSummary = () => {
        if (billData?.order_summary && billData.order_summary.length > 0) {
            return billData.order_summary[0];
        }
        return null;
    };

    // Get order items as array
    const getOrderItems = () => {
        if (!billData?.order_items) return [];
        if (Array.isArray(billData.order_items)) {
            return billData.order_items;
        }
        return [billData.order_items];
    };

    // Get tax summary
    const getTaxSummary = () => {
        return billData?.tax_summary || [];
    };

    // Get receipt header
    const getReceiptHeader = () => {
        if (billData?.receipt_header && billData.receipt_header.length > 0) {
            return billData.receipt_header[0];
        }
        return null;
    };

    // Calculate totals from items
    const calculateTotals = () => {
        const items = getOrderItems();
        let taxable = 0;
        let cgstTotal = 0;
        let sgstTotal = 0;
        let grandTotal = 0;

        items.forEach((item) => {
            const itemTaxable = parseFloat(item.taxable || item.net_amount || 0);
            const taxAmount = parseFloat(item.tax_amount || 0);
            const totalAmount = parseFloat(item.total_amount || 0);
            const gstPer = parseFloat(item.basic_gst_taxper || item.tax_percentage || 0);

            taxable += itemTaxable;

            // Split GST equally between CGST and SGST
            const gstAmount = taxAmount || (itemTaxable * gstPer) / 100;
            cgstTotal += gstAmount / 2;
            sgstTotal += gstAmount / 2;

            grandTotal += totalAmount || itemTaxable + gstAmount;
        });

        return { taxable, cgstTotal, sgstTotal, grandTotal };
    };

    // Get grouped tax rates for display
    const getGroupedTaxRates = () => {
        const items = getOrderItems();
        const taxGroups = {};

        items.forEach((item) => {
            const gstPer = parseFloat(item.basic_gst_taxper || item.tax_percentage || 0);
            const itemTaxable = parseFloat(item.taxable || item.net_amount || 0);
            const halfRate = gstPer / 2;

            if (gstPer > 0) {
                const cgstKey = `CGST(${halfRate}%)`;
                const sgstKey = `SGST(${halfRate}%)`;

                if (!taxGroups[cgstKey]) taxGroups[cgstKey] = 0;
                if (!taxGroups[sgstKey]) taxGroups[sgstKey] = 0;

                taxGroups[cgstKey] += (itemTaxable * halfRate) / 100;
                taxGroups[sgstKey] += (itemTaxable * halfRate) / 100;
            }
        });

        return taxGroups;
    };

    const handlePrint = () => {
        const printContent = document.querySelector('.receipt-content');
        if (!printContent) return;

        // Larger window size for better preview visibility
        const printWindow = window.open('', '_blank', 'width=800,height=900,left=100,top=50');

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Bill Receipt</title>
                <style>
                    @page {
                        size: 80mm auto;
                        margin: 0;
                    }
                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                    }
                    body {
                        font-family: 'Courier New', Courier, monospace;
                        font-size: 12px;
                        line-height: 1.4;
                        color: #000;
                        background: #fff;
                        width: 80mm;
                        padding: 3mm;
                    }
                    .receipt-header { text-align: center; margin-bottom: 8px; }
                    .receipt-title { font-size: 16px; font-weight: bold; margin: 0 0 8px 0; text-transform: uppercase; }
                    .receipt-title.bordered { display: inline-block; border: 2px solid #000; padding: 4px 12px; margin-bottom: 10px; }
                    .company-name { font-size: 15px; font-weight: bold; margin: 0 0 2px 0; text-transform: uppercase; }
                    .company-subtitle { font-size: 12px; margin: 0 0 4px 0; }
                    .company-address { font-size: 11px; margin: 0 0 2px 0; }
                    .company-gstin, .company-info { font-size: 11px; margin: 2px 0; }
                    .receipt-content.format-1 .company-gstin { font-size: 12px; }
                    .header-contact { display: flex; flex-direction: column; align-items: center; gap: 2px; margin-top: 4px; font-size: 11px; }
                    .header-contact-row { display: flex; justify-content: space-between; align-items: center; margin-top: 6px; font-size: 11px; }
                    .receipt-divider { border-top: 1px solid #000; margin: 8px 0; }
                    .receipt-divider.thick { border-top: 2px solid #000; }
                    .invoice-details { margin: 8px 0; }
                    .invoice-row { display: flex; justify-content: space-between; font-size: 11px; margin-bottom: 2px; }
                    .invoice-row-center { text-align: right; font-size: 11px; }
                    .items-table { width: 100%; border-collapse: collapse; font-size: 11px; }
                    .items-table thead tr { border-bottom: 1px solid #000; }
                    .items-table th { font-weight: bold; padding: 4px 2px; text-align: left; }
                    .items-table th.item-name-col { width: 40%; text-align: left; }
                    .items-table th.item-basic-col { width: 22%; text-align: right; }
                    .items-table th.item-qty-col { width: 13%; text-align: center; }
                    .items-table th.item-total-col { width: 25%; text-align: right; }
                    .items-table.format-1 th.item-desc-col { width: 45%; text-align: left; }
                    .items-table.format-1 th.item-price-col { width: 18%; text-align: right; }
                    .items-table tbody tr.item-row { vertical-align: top; border-bottom: 1px dashed #ccc; }
                    .items-table.format-1 .item-row.format-1 td { padding: 6px 2px 2px 2px; border-bottom: none; }
                    .items-table.format-1 .item-price-col { text-align: right; }
                    .items-table.format-1 .item-details-row td { padding: 0 2px 6px 2px; font-size: 9px; border-bottom: 1px dashed #ccc; }
                    .items-table.format-1 .item-hsn-cell { text-align: left; }
                    .items-table.format-1 .item-gst-cell { text-align: right; }
                    .items-table td { padding: 6px 2px; }
                    .items-table td.item-name-col { text-align: left; }
                    .items-table td.item-basic-col { text-align: right; }
                    .items-table td.item-qty-col { text-align: center; }
                    .items-table td.item-total-col { text-align: right; }
                    .item-name { font-weight: bold; font-size: 11px; margin-bottom: 2px; }
                    .item-hsn { font-size: 9px; color: #333; margin-top: 2px; }
                    .item-basic, .item-qty, .item-total, .item-price { font-size: 11px; }
                    .item-gst { font-size: 9px; color: #333; margin-top: 2px; }
                    .totals-section { margin: 8px 0; }
                    .total-row { display: flex; justify-content: flex-end; align-items: center; gap: 20px; font-size: 12px; margin-bottom: 4px; }
                    .total-label { font-weight: bold; min-width: 80px; text-align: right; }
                    .total-value { min-width: 70px; text-align: right; }
                    .grand-total-section { display: flex; justify-content: center; align-items: center; gap: 20px; padding: 10px 0; margin: 8px 0; }
                    .grand-total-section.format-1 { justify-content: space-between; padding: 12px 0; }
                    .grand-total-label { font-size: 18px; font-weight: bold; }
                    .grand-total-section.format-1 .grand-total-label { font-size: 20px; }
                    .grand-total-value { font-size: 24px; font-weight: bold; }
                    .grand-total-section.format-1 .grand-total-value { font-size: 26px; }
                    .gst-info-section { text-align: center; margin: 10px 0; font-size: 11px; }
                    .gst-inclusive-text { margin-bottom: 8px; font-size: 12px; }
                    .gst-line { margin: 2px 0; }
                    .gst-total-line { margin-top: 6px; font-size: 12px; }
                    .receipt-footer { text-align: center; margin-top: 10px; }
                    .receipt-footer.format-1 { margin-top: 10px; }
                    .footer-note { font-size: 10px; margin: 0 0 10px 0; padding: 8px 0; border-top: 1px solid #000; border-bottom: 1px solid #000; }
                    .footer-note.bordered { border: 1px solid #000; border-radius: 4px; padding: 8px; margin: 0; }
                    .thank-you { font-size: 16px; font-weight: bold; margin: 15px 0 10px 0; }
                    .sac-code-section { text-align: center; margin: 8px 0; padding: 6px 0; border-top: 1px solid #000; font-size: 12px; }
                    .sac-code-text { font-size: 12px; }
                </style>
            </head>
            <body>
                ${printContent.innerHTML}
            </body>
            </html>
        `);

        printWindow.document.close();
        printWindow.focus();

        // Wait for content to load before printing
        setTimeout(() => {
            printWindow.print();
            printWindow.close();
        }, 250);
    };

    const handleBack = () => {
        navigate(-1);
    };

    if (loading) {
        return (
            <div className="bill-receipt-loading">
                <ProgressSpinner />
                <p>Loading bill...</p>
            </div>
        );
    }

    if (error || !billData) {
        return (
            <div className="bill-receipt-error">
                <Toast ref={toast} />
                <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem', color: '#f59e0b' }}></i>
                <p>{error || 'Bill data not found'}</p>
                <Button label="Go Back" icon="pi pi-arrow-left" onClick={handleBack} />
            </div>
        );
    }

    const showHSN = shouldShowHSN();
    const invoiceFormat = getInvoiceFormat();
    const hsnSacOption = getHsnSacDisplayOption();
    const sacCode = getUniqueSacCode();
    const orderSummary = getOrderSummary();
    const orderItems = getOrderItems();
    const receiptHeader = getReceiptHeader();
    const totals = calculateTotals();
    const groupedTaxRates = getGroupedTaxRates();

    const formatWaiterId = (value) => {
        if (value === null || value === undefined || String(value).trim() === '') return '-';
        return String(value);
    };

    const renderInvoiceDetails = () => (
        <div className="invoice-details">
            <div className="invoice-row">
                <span>
                    <strong>Invoice No:</strong> {orderSummary?.invoice_no || orderSummary?.bill_no}
                </span>
                <span>
                    <strong>Date:</strong> {formatDate(orderSummary?.invoice_date)}
                </span>
            </div>
            <div className="invoice-row">
                <span>
                    <strong>Waiter ID:</strong> {formatWaiterId(waiterIdFromNav)}
                </span>
                <span>
                    <strong>Time:</strong> {orderSummary?.invoice_time}
                </span>
            </div>
        </div>
    );

    // Get tax breakdown for format 1
    const getTaxBreakdown = () => {
        const items = getOrderItems();
        const taxBreakdown = {};

        items.forEach((item) => {
            const gstPer = parseFloat(item.basic_gst_taxper || item.tax_percentage || 0);
            const itemTaxable = parseFloat(item.taxable || item.net_amount || 0);
            const halfRate = gstPer / 2;

            if (gstPer > 0) {
                const key = halfRate;
                if (!taxBreakdown[key]) {
                    taxBreakdown[key] = { rate: halfRate, taxable: 0, cgst: 0, sgst: 0 };
                }
                taxBreakdown[key].taxable += itemTaxable;
                taxBreakdown[key].cgst += (itemTaxable * halfRate) / 100;
                taxBreakdown[key].sgst += (itemTaxable * halfRate) / 100;
            }
        });

        return Object.values(taxBreakdown);
    };

    // Render Format 0 (Default)
    const renderFormat0 = () => {
        // Check if we should show HSN inline (option 1) or SAC at bottom (option 2)
        const showHsnInline = hsnSacOption === 1;
        const showSacAtBottom = hsnSacOption === 2;

        return (
            <div className="receipt-content">
                {/* Header Section */}
                <div className="receipt-header">
                    {showHSN && <h2 className="receipt-title">Tax Invoice</h2>}

                    <h3 className="company-name">{receiptHeader?.branch_title || receiptHeader?.org_name || 'Company Name'}</h3>
                    <p className="company-subtitle">{receiptHeader?.org_name}</p>

                    {receiptHeader?.address1 && <p className="company-address">{receiptHeader.address1}</p>}
                    {receiptHeader?.address2 && <p className="company-address">{receiptHeader.address2}</p>}

                    {receiptHeader?.gstin_no && (
                        <p className="company-gstin">
                            <strong>GSTIN :</strong> {receiptHeader.gstin_no}
                        </p>
                    )}

                    {receiptHeader?.llp_in && receiptHeader.llp_in !== '0,' && receiptHeader.llp_in !== '0' && (
                        <p className="company-info">
                            <strong>LLP IN :</strong> {receiptHeader.llp_in}
                        </p>
                    )}

                    {receiptHeader?.cin_no && receiptHeader.cin_no !== '0,' && receiptHeader.cin_no !== '0' && (
                        <p className="company-info">
                            <strong>CIN NO :</strong> {receiptHeader.cin_no}
                        </p>
                    )}

                    {receiptHeader?.fssai_no && receiptHeader.fssai_no !== '0' && (
                        <p className="company-info">
                            <strong>FSSAI :</strong> {receiptHeader.fssai_no}
                        </p>
                    )}

                    <div className="header-contact-row">
                        {receiptHeader?.website && <span className="contact-website">{receiptHeader.website}</span>}
                        {receiptHeader?.mobile && <span className="contact-phone">{receiptHeader.mobile}</span>}
                    </div>
                </div>

                <div className="receipt-divider"></div>

                {renderInvoiceDetails()}

                <div className="receipt-divider"></div>

                {/* Items Table */}
                <table className="items-table">
                    <thead>
                        <tr>
                            <th className="item-name-col">Item</th>
                            <th className="item-basic-col">Basic</th>
                            <th className="item-qty-col">Qty</th>
                            <th className="item-total-col">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orderItems.map((item, index) => {
                            const gstPer = parseFloat(item.basic_gst_taxper || item.tax_percentage || 0);
                            const halfGst = gstPer / 2;
                            const itemHasHSN = hasValidHSN(item.hsn_code);

                            return (
                                <tr key={index} className="item-row">
                                    <td className="item-name-col">
                                        <div className="item-name">{item.product_name}</div>
                                        {showHsnInline && showHSN && itemHasHSN && <div className="item-hsn">HSN code: {item.hsn_code}</div>}
                                    </td>
                                    <td className="item-basic-col">
                                        <div className="item-basic">{formatCurrency(item.basic_rate || item.basic_mrp)}</div>
                                        {showHsnInline && gstPer > 0 && <div className="item-gst">CGST :{halfGst}%</div>}
                                    </td>
                                    <td className="item-qty-col">
                                        <div className="item-qty">{item.quantity}</div>
                                    </td>
                                    <td className="item-total-col">
                                        <div className="item-total">{formatCurrency(item.total_amount || item.net_amount)}</div>
                                        {showHsnInline && gstPer > 0 && <div className="item-gst">SGST : {halfGst}%</div>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                <div className="receipt-divider"></div>

                {/* Totals Section */}
                <div className="totals-section">
                    <div className="total-row">
                        <span className="total-label">Total :</span>
                        <span className="total-value">{formatCurrency(totals.taxable)}</span>
                    </div>

                    {Object.entries(groupedTaxRates).map(([label, value], idx) => (
                        <div className="total-row" key={idx}>
                            <span className="total-label">{label}:</span>
                            <span className="total-value">{formatCurrency(value)}</span>
                        </div>
                    ))}
                </div>

                {/* SAC Code - shown when HSN_OR_SAC_DISPLAY_OPTION is 2 */}
                {showSacAtBottom && sacCode && (
                    <div className="sac-code-section">
                        <span className="sac-code-text">
                            <strong>SAC code:</strong>
                            {sacCode}
                        </span>
                    </div>
                )}

                <div className="receipt-divider thick"></div>

                {/* Grand Total */}
                <div className="grand-total-section">
                    <span className="grand-total-label">Grand Total:</span>
                    <span className="grand-total-value">{Math.round(totals.grandTotal)}</span>
                </div>

                {/* <div className="receipt-divider"></div> */}

                {/* Footer */}
                <div className="receipt-footer">
                    <p className="footer-note">Computer generated invoice, hence signature not required</p>
                    <h3 className="thank-you">Thank You.</h3>
                </div>
            </div>
        );
    };

    // Render Format 1 (Alternate - GST Inclusive)
    const renderFormat1 = () => {
        const taxBreakdown = getTaxBreakdown();
        const totalGST = totals.cgstTotal + totals.sgstTotal;
        // Check if we should show HSN inline (option 1) or SAC at bottom (option 2)
        const showHsnInline = hsnSacOption === 1;
        const showSacAtBottom = hsnSacOption === 2;

        return (
            <div className="receipt-content format-1">
                {/* Header Section */}
                <div className="receipt-header">
                    {showHSN && <h2 className="receipt-title bordered">Tax Invoice</h2>}

                    <h3 className="company-name">{receiptHeader?.branch_title || receiptHeader?.org_name || 'Company Name'}</h3>
                    <p className="company-subtitle">{receiptHeader?.org_name}</p>

                    {receiptHeader?.address1 && <p className="company-address">{receiptHeader.address1}</p>}
                    {receiptHeader?.address2 && <p className="company-address">{receiptHeader.address2}</p>}

                    {receiptHeader?.gstin_no && (
                        <p className="company-gstin">
                            <strong>GSTIN :</strong>
                            {receiptHeader.gstin_no}
                        </p>
                    )}

                    {receiptHeader?.cin_no && receiptHeader.cin_no !== '0,' && (
                        <p className="company-info">
                            <strong>LLP IN :</strong> {receiptHeader.cin_no}
                        </p>
                    )}

                    {receiptHeader?.fssai_no && receiptHeader.fssai_no !== '0' && (
                        <p className="company-info">
                            <strong>FSSAI :</strong> {receiptHeader.fssai_no}
                        </p>
                    )}

                    <div className="header-contact-row">
                        {receiptHeader?.website && <span className="contact-website">{receiptHeader.website}</span>}
                        {receiptHeader?.mobile && <span className="contact-phone">{receiptHeader.mobile}</span>}
                    </div>
                </div>

                <div className="receipt-divider"></div>

                {renderInvoiceDetails()}

                <div className="receipt-divider"></div>

                {/* Items Table - Format 1 */}
                <table className="items-table format-1">
                    <thead>
                        <tr>
                            <th className="item-desc-col">Description</th>
                            <th className="item-price-col">Price</th>
                            <th className="item-qty-col">Qty</th>
                            <th className="item-total-col">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orderItems.map((item, index) => {
                            const gstPer = parseFloat(item.basic_gst_taxper || item.tax_percentage || 0);
                            const halfGst = gstPer / 2;
                            const itemHasHSN = hasValidHSN(item.hsn_code);
                            const itemTotal = parseFloat(item.total_amount || item.net_amount || 0);

                            return (
                                <React.Fragment key={index}>
                                    <tr className="item-row format-1">
                                        <td className="item-desc-col">
                                            <div className="item-name">{item.product_name}</div>
                                        </td>
                                        <td className="item-price-col">
                                            <div className="item-price">{Math.round(itemTotal)}</div>
                                        </td>
                                        <td className="item-qty-col">
                                            <div className="item-qty">{item.quantity}</div>
                                        </td>
                                        <td className="item-total-col">
                                            <div className="item-total">{Math.round(itemTotal)}</div>
                                        </td>
                                    </tr>
                                    {showHsnInline && ((showHSN && itemHasHSN) || gstPer > 0) ? (
                                        <tr className="item-details-row">
                                            <td colSpan="2" className="item-hsn-cell">
                                                {showHSN && itemHasHSN && <span>HSN code: {item.hsn_code}</span>}
                                            </td>
                                            <td colSpan="2" className="item-gst-cell">
                                                {gstPer > 0 && (
                                                    <span>
                                                        CGST :{halfGst}% &nbsp;&nbsp; SGST : {halfGst}%
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ) : null}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>

                {/* SAC Code - shown when HSN_OR_SAC_DISPLAY_OPTION is 2 */}
                {showSacAtBottom && sacCode && (
                    <div className="sac-code-section">
                        <span className="sac-code-text">
                            <strong>SAC code:</strong>
                            {sacCode}
                        </span>
                    </div>
                )}

                <div className="receipt-divider thick"></div>

                {/* Grand Total */}
                <div className="grand-total-section format-1">
                    <span className="grand-total-label">Grand Total:</span>
                    <span className="grand-total-value">{Math.round(totals.grandTotal)}</span>
                </div>

                <div className="receipt-divider"></div>

                {/* GST Information */}
                <div className="gst-info-section">
                    <p className="gst-inclusive-text">
                        <strong>ABOVE PRICES INCLUDE GST</strong>
                    </p>
                    {taxBreakdown.map((tax, idx) => (
                        <React.Fragment key={idx}>
                            <p className="gst-line">
                                CGST @ {tax.rate} % ON {formatCurrency(tax.taxable)} : {formatCurrency(tax.cgst)}
                            </p>
                            <p className="gst-line">
                                SGST @ {tax.rate} % ON {formatCurrency(tax.taxable)} : {formatCurrency(tax.sgst)}
                            </p>
                        </React.Fragment>
                    ))}
                    <p className="gst-total-line">
                        <strong>Total GST : {formatCurrency(totalGST)}</strong>
                    </p>
                </div>

                {/* Footer */}
                <div className="receipt-footer format-1">
                    <p className="footer-note bordered">Computer generated invoice, hence signature not required</p>
                    {showSacAtBottom && <h3 className="thank-you">Thank You.</h3>}
                </div>
            </div>
        );
    };

    const orderPayments = billData?.order_payments != null ? (Array.isArray(billData.order_payments) ? billData.order_payments : [billData.order_payments]) : [];
    const receiptSettings = billData?.receipt_settings || [];

    return (
        <div className="bill-receipt-container">
            <Toast ref={toast} />

            {/* Action Buttons - Not printed */}
            <div className="bill-receipt-actions no-print">
                <Button label="Back" icon="pi pi-arrow-left" className="p-button-secondary" onClick={handleBack} />
                <Button label="Print" icon="pi pi-print" className="p-button-info" onClick={handlePrint} />
            </div>

            {/* Layout: Receipt left, Options right */}
            <div className="bill-receipt-layout">
                <div className="receipt-wrapper receipt-left">{invoiceFormat === 1 ? renderFormat1() : renderFormat0()}</div>
                <BillViewReceiptSidePanel
                    billId={id}
                    branchId={branchId}
                    orderPayments={orderPayments}
                    receiptSettings={receiptSettings}
                    orderSummary={orderSummary}
                    editedBy={storedUserid}
                    onBillCancelled={fetchBillData}
                    onPaymentModeUpdated={fetchBillData}
                    toastRef={toast}
                />
            </div>
        </div>
    );
};

export default BillViewReceipt;
